import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bot, Plus, Trash2, RefreshCw, Sparkles, MessageCircle, AlertTriangle, Upload, Globe, Check, FileText, Send, Zap, CheckCircle2, Loader2 } from 'lucide-react';

const Stat = ({ label, value }) => (
  <div className="rounded-lg border p-3">
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

// Minimal RFC-4180 CSV parser (handles quoted fields with commas/newlines).
function parseCSV(text) {
  const rows = [];
  let field = '', row = [], inQ = false, i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); rows.push(row); row = []; field = '';
    } else field += c;
    i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(c => c.trim() !== ''));
}

export default function AiAssistant() {  const [status, setStatus] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [usage, setUsage] = useState(null);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', priority: 1 });
  const [details, setDetails] = useState(null);
  const [settings, setSettings] = useState({ name: '', greeting_message: '', primary_color: '#4f46e5', fallback_message: '', fallback_cta_label: '', fallback_cta_url: '', footer_enabled: false, footer_message: '', footer_cta_label: '', footer_cta_url: '', widget_scope: 'all', widget_paths: [] });
  const [savingSettings, setSavingSettings] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // { rows: [{question,answer}] }
  const fileInputRef = React.useRef(null);
  const [crawl, setCrawl] = useState(null); // { base_url, pages: [{title,url,type,included}], last_crawled_at }
  const [savingPages, setSavingPages] = useState(false);
  const [linkPages, setLinkPages] = useState([]); // [{title, path}] for CTA picker
  const [documents, setDocuments] = useState(null); // { documents: [{media_id, filename, included, ...}], synced_count }
  const [savingDocs, setSavingDocs] = useState(false);
  // Test Bot chat panel
  const [testMessages, setTestMessages] = useState([]); // [{role:'user'|'bot', text, cta, footer, sources, error}]
  const [testInput, setTestInput] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testSession, setTestSession] = useState(null);
  const testScrollRef = React.useRef(null);

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) { toast.error('That CSV looks empty.'); return; }
      // Drop header row if it looks like headers
      let start = 0;
      const first = (rows[0][0] || '').trim().toLowerCase();
      if (first === 'question') start = 1;
      const items = rows.slice(start)
        .map(r => ({ question: (r[0] || '').trim(), answer: (r[1] || '').trim() }))
        .filter(r => r.question && r.answer);
      if (!items.length) { toast.error('No valid rows. Use two columns: question, answer.'); return; }
      setPendingImport({ rows: items });
    } catch (err) {
      toast.error('Could not read that file.');
    }
  };

  const runImport = async (mode) => {
    try {
      const res = await api.post('/tenant/faqs/import', { mode, items: pendingImport.rows });
      toast.success(`Imported ${res.data.imported} FAQ(s). Click "Sync now" to push to your assistant.`);
      setPendingImport(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    }
  };

  const downloadSample = () => {
    const csv = 'question,answer\nWhat are your school hours?,"Our day runs 8:30am to 3:15pm, Monday to Friday."\nHow do I enrol?,Visit the Admissions page and complete the online form.\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'faq-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const loadDetails = async () => {
    try {
      const r = await api.get('/tenant/ai-assistant/details');
      setDetails(r.data);
      if (r.data?.config) {
        setSettings({
          name: r.data.config.name || '',
          greeting_message: r.data.config.greeting_message || '',
          primary_color: r.data.config.primary_color || '#4f46e5',
          fallback_message: r.data.config.fallback_message || '',
          fallback_cta_label: r.data.config.fallback_cta_label || '',
          fallback_cta_url: r.data.config.fallback_cta_url || '',
          footer_enabled: !!r.data.config.footer_enabled,
          footer_message: r.data.config.footer_message || '',
          footer_cta_label: r.data.config.footer_cta_label || '',
          footer_cta_url: r.data.config.footer_cta_url || '',
          widget_scope: r.data.config.widget_scope || 'all',
          widget_paths: r.data.config.widget_paths || [],
        });
      }
    } catch (e) { /* non-fatal */ }
  };

  const loadCrawlPages = async () => {
    try {
      const r = await api.get('/tenant/ai-assistant/pages');
      setCrawl(r.data);
    } catch (e) { /* non-fatal */ }
    try {
      const lp = await api.get('/tenant/ai-assistant/link-pages');
      setLinkPages(lp.data?.pages || []);
    } catch (e) { /* non-fatal */ }
  };

  const toggleCrawlPage = (url) => {
    setCrawl((prev) => prev && ({
      ...prev,
      pages: prev.pages.map((p) => (p.url === url ? { ...p, included: !p.included } : p)),
    }));
  };

  const setAllCrawlPages = (included) => {
    setCrawl((prev) => prev && ({
      ...prev,
      pages: prev.pages.map((p) => ({ ...p, included })),
    }));
  };

  const sendTestMessage = async () => {
    const msg = testInput.trim();
    if (!msg || testSending) return;
    setTestMessages((prev) => [...prev, { role: 'user', text: msg }]);
    setTestInput('');
    setTestSending(true);
    try {
      const res = await api.post('/tenant/ai-assistant/test-chat', { message: msg, session_id: testSession });
      if (res.data.session_id) setTestSession(res.data.session_id);
      setTestMessages((prev) => [...prev, {
        role: 'bot',
        text: res.data.response,
        cta: res.data.cta,
        footer: res.data.footer,
        sources: res.data.sources,
      }]);
    } catch (e) {
      setTestMessages((prev) => [...prev, {
        role: 'bot',
        text: e?.response?.data?.detail || 'The assistant could not answer right now.',
        error: true,
      }]);
    } finally {
      setTestSending(false);
    }
  };

  const resetTestChat = () => {
    setTestMessages([]);
    setTestSession(null);
    setTestInput('');
  };

  React.useEffect(() => {
    if (testScrollRef.current) {
      testScrollRef.current.scrollTop = testScrollRef.current.scrollHeight;
    }
  }, [testMessages, testSending]);

  const loadDocuments = async () => {
    try {
      const r = await api.get('/tenant/ai-assistant/documents');
      setDocuments(r.data);
    } catch (e) { /* non-fatal */ }
  };

  const toggleDocument = (mediaId) => {
    setDocuments((prev) => prev && ({
      ...prev,
      documents: prev.documents.map((d) => (d.media_id === mediaId ? { ...d, included: !d.included } : d)),
    }));
  };

  const setAllDocuments = (included) => {
    setDocuments((prev) => prev && ({
      ...prev,
      documents: prev.documents.map((d) => ({ ...d, included })),
    }));
  };

  const handleSaveDocuments = async () => {
    if (!documents) return;
    setSavingDocs(true);
    try {
      const media_ids = documents.documents.filter((d) => d.included).map((d) => d.media_id);
      const res = await api.put('/tenant/ai-assistant/documents', { media_ids });
      const parts = [];
      if (res.data.added) parts.push(`${res.data.added} added`);
      if (res.data.removed) parts.push(`${res.data.removed} removed`);
      toast.success(`Documents updated${parts.length ? ' — ' + parts.join(', ') : ''}. ${res.data.synced_count} PDF(s) now feed your assistant.`);
      (res.data.errors || []).forEach((err) => {
        toast.error(`${err.filename || 'A file'}: ${err.error}`);
      });
      await loadDocuments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update documents.');
    } finally {
      setSavingDocs(false);
    }
  };

  const handleSaveCrawlPages = async () => {
    if (!crawl) return;
    setSavingPages(true);
    try {
      const excluded_urls = crawl.pages.filter((p) => !p.included).map((p) => p.url);
      const res = await api.put('/tenant/ai-assistant/pages', { excluded_urls, recrawl: true });
      toast.success(`Saved — ${res.data.included} page(s) are now part of your assistant's knowledge.`);
      if (res.data.qa_error) {
        toast.error('Could not update the assistant knowledge: ' + res.data.qa_error);
      }
      await loadCrawlPages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save page selection.');
    } finally {
      setSavingPages(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/tenant/ai-assistant/settings', settings);
      toast.success('Bot settings updated.');
      await loadDetails();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  // Reusable CTA fields (label + URL with internal-page picker) for the
  // Fallback and Footer sections. `prefix` is 'fallback' or 'footer'.
  const renderCtaFields = (prefix, labelPlaceholder = 'Contact us') => {
    const labelKey = `${prefix}_cta_label`;
    const urlKey = `${prefix}_cta_url`;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>CTA button label <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input
            value={settings[labelKey]}
            onChange={(e) => setSettings({ ...settings, [labelKey]: e.target.value })}
            placeholder={labelPlaceholder}
            data-testid={`bot-${prefix}-cta-label-input`}
          />
        </div>
        <div className="space-y-1.5">
          <Label>CTA button URL</Label>
          {linkPages.length > 0 && (
            <Select
              value={linkPages.some((p) => p.path === settings[urlKey]) ? settings[urlKey] : ''}
              onValueChange={(v) => setSettings({ ...settings, [urlKey]: v })}
            >
              <SelectTrigger data-testid={`bot-${prefix}-cta-page-select`}>
                <SelectValue placeholder="Pick one of your pages…" />
              </SelectTrigger>
              <SelectContent>
                {linkPages.map((p) => (
                  <SelectItem key={p.path} value={p.path} data-testid={`bot-${prefix}-cta-page-${p.path}`}>
                    {p.title} <span className="text-slate-400">({p.path})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input
            value={settings[urlKey]}
            onChange={(e) => setSettings({ ...settings, [urlKey]: e.target.value })}
            placeholder="/contact or https://yoursite.com/contact"
            data-testid={`bot-${prefix}-cta-url-input`}
          />
          <p className="text-xs text-slate-400">Pick an internal page, or type any link (page path, external URL, mailto:, tel:).</p>
        </div>
      </div>
    );
  };


  const loadAll = async () => {
    try {
      const [s, f] = await Promise.all([
        api.get('/tenant/ai-assistant'),
        api.get('/tenant/faqs'),
      ]);
      setStatus(s.data);
      setFaqs(f.data || []);
      if (s.data?.enabled && s.data?.bot_id) {
        loadDetails();
        loadCrawlPages();
        loadDocuments();
        // Load usage meter
        api.get('/tenant/ai-assistant/usage').then(r => setUsage(r.data)).catch(() => {});
      }
    } catch (e) {
      toast.error('Could not load AI Assistant settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Handle return from Stripe add-on checkout
    const params = new URLSearchParams(window.location.search);
    const addonSession = params.get('addon_session');
    if (addonSession) {
      api.post('/tenant/ai-assistant/verify-addon', { session_id: addonSession })
        .then((r) => {
          if (r.data?.activated) toast.success('AI Assistant add-on activated! You can now enable it below.');
          else toast.info('Payment is processing. It may take a moment to activate.');
        })
        .catch(() => {})
        .finally(() => {
          window.history.replaceState({}, '', '/ai-assistant');
          loadAll();
        });
    } else {
      loadAll();
    }
  }, []);

  const handleSubscribe = async () => {
    setBusy(true);
    try {
      const res = await api.post('/tenant/ai-assistant/subscribe');
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  const handleToggle = async (checked) => {
    setBusy(true);
    try {
      if (checked) {
        const res = await api.post('/tenant/ai-assistant/enable', {
          website_url: window.location.origin,
        });
        toast.success(`AI Assistant enabled. ${res.data.qa_synced} Q&A entries synced.`);
        if (res.data.crawl_error) {
          toast.info('Website crawling is not active on your current plan yet — the assistant will answer from your FAQs and articles.');
        }
      } else {
        await api.post('/tenant/ai-assistant/disable');
        toast.success('AI Assistant disabled. The chat widget is hidden from your site.');
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the AI Assistant.');
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async () => {
    setBusy(true);
    try {
      const res = await api.post('/tenant/ai-assistant/sync');
      toast.success(`Synced ${res.data.qa_synced} Q&A entries to your assistant.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const res = await api.post('/tenant/ai-assistant/provision', {
        website_url: window.location.origin,
      });
      if (res.data.already_provisioned) {
        toast.info('AI Assistant bot is already provisioned.');
      } else {
        toast.success('AI Assistant bot provisioned! You can now enable the chat widget below.');
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not provision the AI Assistant.');
    } finally {
      setProvisioning(false);
    }
  };

  const handleAddFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      toast.error('Both a question and an answer are required.');
      return;
    }
    try {
      await api.post('/tenant/faqs', newFaq);
      setNewFaq({ question: '', answer: '', priority: 1 });
      toast.success('FAQ added. Click "Sync now" to push it to your assistant.');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add FAQ.');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    try {
      await api.delete(`/tenant/faqs/${faqId}`);
      await loadAll();
    } catch (e) {
      toast.error('Could not delete FAQ.');
    }
  };

  const renderAddonOffer = () => (
    <Card className="border-indigo-200" data-testid="ai-addon-card">
      <CardContent className="p-8 text-center">
        <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-indigo-600 flex items-center justify-center text-white">
          <Bot size={28} />
        </div>
        <h2 className="text-xl font-bold mb-2">Add the AI Assistant to your site</h2>
        <p className="text-slate-600 max-w-md mx-auto mb-1">
          A smart chatbot that answers your visitors from your FAQs and published content — set up in minutes, no code.
        </p>
        <p className="text-3xl font-bold my-4">${status?.addon_price ?? 39}<span className="text-base font-normal text-slate-500">/month</span></p>
        <p className="text-xs text-slate-500 mb-5">Cancel anytime. Included free on the Pro plan and above.</p>
        <Button onClick={handleSubscribe} disabled={busy} size="lg" data-testid="ai-subscribe-btn" className="gap-2">
          <Sparkles size={18} /> {busy ? 'Redirecting…' : 'Add AI Assistant'}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex" data-testid="ai-assistant-page">
      <Sidebar />
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
              <Bot size={22} />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Assistant</h1>
              <p className="text-slate-500 text-sm">A website chatbot that answers from your FAQs and published content.</p>
            </div>
          </div>

          {loading ? (
            <div className="text-slate-400 animate-pulse">Loading…</div>
          ) : !status?.available ? (
            renderAddonOffer()
          ) : (
            <div className="space-y-6">
              {status?.addon_active && !status?.included && (
                <div className="flex items-center gap-2 text-sm text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2" data-testid="ai-addon-active-banner">
                  <Sparkles size={15} /> AI Assistant add-on active (${status.addon_price}/mo). Manage billing in Settings.
                </div>
              )}
              {status?.included && (
                <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-2" data-testid="ai-included-banner">
                  <Sparkles size={15} /> Included with your {status.plan_level} plan.
                </div>
              )}
              {/* Provision card — shown when entitled but bot not yet created */}
              {!status?.provisioned && (
                <Card className="border-indigo-200 bg-indigo-50/40" data-testid="ai-provision-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Zap size={18} className="text-indigo-600" /> Set Up AI Assistant
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Provision your AI Assistant bot to get started. This creates a dedicated chatbot trained on your site content — takes just a second.
                    </p>
                    {status?.provision_status === 'error' && (
                      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="ai-provision-error">
                        <AlertTriangle size={14} /> Provisioning failed. Please try again.
                      </div>
                    )}
                    <Button
                      onClick={handleProvision}
                      disabled={provisioning}
                      className="gap-2"
                      data-testid="ai-provision-btn"
                    >
                      {provisioning ? (
                        <><Loader2 size={15} className="animate-spin" /> Provisioning…</>
                      ) : (
                        <><Zap size={15} /> Provision AI Assistant</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Usage Meter card — shown when bot is enabled */}
              {status?.enabled && usage && (
                <Card data-testid="ai-usage-card">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between text-base">
                      <span className="flex items-center gap-2">
                        <Zap size={16} className="text-indigo-500" /> Usage This Month
                      </span>
                      <span className="text-sm font-normal text-slate-500">{usage.period_start} → {usage.period_end}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Progress bar */}
                    {(() => {
                      const pct = Math.min(100, Math.round((usage.query_count / usage.included) * 100));
                      const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
                      return (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-slate-700">{usage.query_count.toLocaleString()} <span className="text-slate-400 font-normal">/ {usage.included.toLocaleString()} included queries</span></span>
                            <span className={`font-semibold ${pct >= 100 ? 'text-red-600' : pct >= 80 ? 'text-amber-600' : 'text-emerald-600'}`}>{pct}%</span>
                          </div>
                          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} data-testid="ai-usage-bar" />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Overage warning */}
                    {usage.overage_queries > 0 && (
                      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm" data-testid="ai-overage-warning">
                        <AlertTriangle size={15} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-700">{usage.overage_queries.toLocaleString()} queries over your limit</p>
                          <p className="text-red-600 text-xs mt-0.5">
                            Estimated extra charge: <strong>${usage.estimated_overage_charge.toFixed(2)}</strong> — billed automatically on the 1st of next month (${usage.rate_per_100.toFixed(2)} per 100 queries)
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Approaching limit warning */}
                    {usage.overage_queries === 0 && usage.query_count / usage.included >= 0.8 && (
                      <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700" data-testid="ai-usage-warning">
                        <AlertTriangle size={14} className="flex-shrink-0" />
                        Approaching your monthly limit — {usage.included - usage.query_count} queries remaining.
                      </div>
                    )}

                    <p className="text-xs text-slate-400">
                      Plan: <span className="capitalize">{usage.addon_tier}</span> tier — {usage.included.toLocaleString()} queries/month included. Overages billed at ${usage.rate_per_100.toFixed(2)} per 100 queries.
                    </p>
                  </CardContent>
                </Card>
              )}
              {status?.provisioned && !status?.enabled && (
                <div className="flex items-center gap-2 text-sm text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2" data-testid="ai-provisioned-banner">
                  <CheckCircle2 size={15} /> Bot provisioned and ready — enable the chat widget below to go live.
                </div>
              )}

              {/* Enable card */}
              <Card data-testid="ai-enable-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Sparkles size={18} /> Chatbot</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Enable AI Assistant on your website</p>
                      <p className="text-sm text-slate-500">
                        {status.enabled
                          ? 'Live — a chat bubble appears on your public site.'
                          : status.provisioned
                            ? 'Turn on to add a chat bubble to every page of your site.'
                            : 'Provision your bot above first, then enable it here.'}
                      </p>
                    </div>
                    <Switch
                      checked={!!status.enabled}
                      disabled={busy || !status.provisioned}
                      onCheckedChange={handleToggle}
                      data-testid="ai-enable-toggle"
                    />
                  </div>

                  {status.enabled && (
                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
                      <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600">
                        <MessageCircle size={15} /> Bot connected
                      </span>
                      <span className="text-sm text-slate-400">•</span>
                      <span className="text-sm text-slate-600">{status.faq_count} curated FAQ(s)</span>
                      <Button
                        variant="outline" size="sm" onClick={handleSync} disabled={busy}
                        className="ml-auto gap-2" data-testid="ai-sync-btn"
                      >
                        <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Sync now
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Test Bot — chat with your bot right here */}
              {status.bot_id && (
                <Card data-testid="ai-test-bot-card">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-base"><Bot size={18} /> Test Bot</CardTitle>
                        <p className="text-sm text-slate-500 mt-1">
                          Ask a question to try your assistant — same answers your visitors get, without leaving this page.
                        </p>
                      </div>
                      {testMessages.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={resetTestChat} data-testid="ai-test-reset-btn">
                          <RefreshCw size={14} className="mr-1" /> Reset
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div
                      ref={testScrollRef}
                      className="h-72 overflow-y-auto rounded-lg border bg-slate-50 p-3 space-y-3"
                      data-testid="ai-test-messages"
                    >
                      {/* Greeting bubble */}
                      {settings.greeting_message && (
                        <div className="flex justify-start">
                          <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white border px-3 py-2 text-sm text-slate-700 shadow-sm">
                            {settings.greeting_message}
                          </div>
                        </div>
                      )}
                      {testMessages.length === 0 && !settings.greeting_message && (
                        <p className="text-sm text-slate-400 text-center py-6">Type a question below to test your assistant.</p>
                      )}
                      {testMessages.map((m, i) => (
                        m.role === 'user' ? (
                          <div key={i} className="flex justify-end">
                            <div
                              className="max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2 text-sm text-white shadow-sm"
                              style={{ backgroundColor: settings.primary_color || '#4f46e5' }}
                              data-testid={`ai-test-user-msg-${i}`}
                            >
                              {m.text}
                            </div>
                          </div>
                        ) : (
                          <div key={i} className="flex justify-start">
                            <div className={`max-w-[80%] rounded-2xl rounded-tl-sm border px-3 py-2 text-sm shadow-sm ${m.error ? 'bg-red-50 border-red-200 text-red-600' : 'bg-white text-slate-700'}`} data-testid={`ai-test-bot-msg-${i}`}>
                              <p className="whitespace-pre-wrap">{m.text}</p>
                              {m.cta?.label && m.cta?.url && (
                                <a href={m.cta.url} target="_blank" rel="noreferrer"
                                   className="inline-block mt-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                                   style={{ backgroundColor: settings.primary_color || '#4f46e5' }}
                                   data-testid={`ai-test-cta-${i}`}>
                                  {m.cta.label}
                                </a>
                              )}
                              {m.footer && (m.footer.message || m.footer.cta) && (
                                <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                                  {m.footer.message && <p>{m.footer.message}</p>}
                                  {m.footer.cta?.label && m.footer.cta?.url && (
                                    <a href={m.footer.cta.url} target="_blank" rel="noreferrer"
                                       className="inline-block mt-1 font-medium underline"
                                       style={{ color: settings.primary_color || '#4f46e5' }}>
                                      {m.footer.cta.label}
                                    </a>
                                  )}
                                </div>
                              )}
                              {Array.isArray(m.sources) && m.sources.length > 0 && (
                                <p className="mt-1.5 text-[10px] text-slate-400 truncate">Source: {m.sources[0].title || m.sources[0].url}</p>
                              )}
                            </div>
                          </div>
                        )
                      ))}
                      {testSending && (
                        <div className="flex justify-start">
                          <div className="rounded-2xl rounded-tl-sm bg-white border px-3 py-2 text-sm text-slate-400 shadow-sm">
                            <span className="inline-flex gap-1">
                              <span className="animate-bounce">•</span>
                              <span className="animate-bounce" style={{ animationDelay: '0.15s' }}>•</span>
                              <span className="animate-bounce" style={{ animationDelay: '0.3s' }}>•</span>
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        value={testInput}
                        onChange={(e) => setTestInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendTestMessage(); } }}
                        placeholder="Ask your assistant a question…"
                        disabled={testSending}
                        data-testid="ai-test-input"
                      />
                      <Button onClick={sendTestMessage} disabled={testSending || !testInput.trim()} className="gap-1.5 shrink-0" data-testid="ai-test-send-btn">
                        <Send size={15} /> Send
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Bot settings + usage (when enabled) */}
              {status.enabled && details?.bot_id && (
                <div className="space-y-6">
                  <Card data-testid="ai-settings-card">
                    <CardHeader><CardTitle>Bot Settings</CardTitle></CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label>Bot name</Label>
                          <Input
                            value={settings.name}
                            onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                            data-testid="bot-name-input"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Accent colour</Label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={settings.primary_color}
                              onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                              className="h-9 w-14 rounded border cursor-pointer"
                              data-testid="bot-color-input"
                            />
                            <span className="text-sm text-slate-500">{settings.primary_color}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Greeting message</Label>
                        <Textarea
                          rows={2}
                          value={settings.greeting_message}
                          onChange={(e) => setSettings({ ...settings, greeting_message: e.target.value })}
                          data-testid="bot-greeting-input"
                        />
                      </div>

                      {/* Fallback (no answer) */}
                      <div className="pt-5 border-t space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-800">When the bot can't answer</h4>
                          <p className="text-xs text-slate-400">Shown only when the assistant can't find an answer.</p>
                        </div>
                        <div className="space-y-1.5">
                          <Label>Fallback message</Label>
                          <Textarea
                            rows={2}
                            value={settings.fallback_message}
                            onChange={(e) => setSettings({ ...settings, fallback_message: e.target.value })}
                            placeholder="Sorry, I couldn't find an answer. Our team is happy to help."
                            data-testid="bot-fallback-message-input"
                          />
                        </div>
                        {renderCtaFields('fallback', 'Contact us')}
                      </div>

                      {/* Message footer (all answers) */}
                      <div className="pt-5 border-t space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="text-sm font-semibold text-slate-800">Message footer</h4>
                            <p className="text-xs text-slate-400">Shown under every answered message (e.g. a note + a CTA).</p>
                          </div>
                          <Switch
                            checked={settings.footer_enabled}
                            onCheckedChange={(v) => setSettings({ ...settings, footer_enabled: v })}
                            data-testid="bot-footer-enabled-switch"
                          />
                        </div>
                        {settings.footer_enabled && (
                          <div className="space-y-4">
                            <div className="space-y-1.5">
                              <Label>Footer message <span className="text-slate-400 font-normal">(optional)</span></Label>
                              <Textarea
                                rows={2}
                                value={settings.footer_message}
                                onChange={(e) => setSettings({ ...settings, footer_message: e.target.value })}
                                placeholder="Need more help? Reach out to our team."
                                data-testid="bot-footer-message-input"
                              />
                            </div>
                            {renderCtaFields('footer', 'Get in touch')}
                          </div>
                        )}
                      </div>

                      {/* Widget placement */}
                      <div className="pt-5 border-t space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-800">Where the chat bubble appears</h4>
                          <p className="text-xs text-slate-400">Choose whether the bot shows on your whole site or only on chosen pages.</p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, widget_scope: 'all' })}
                            className={`flex-1 text-left rounded-lg border p-3 transition-colors ${settings.widget_scope === 'all' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                            data-testid="widget-scope-all"
                          >
                            <p className="text-sm font-medium">All pages</p>
                            <p className="text-xs text-slate-500">Show on every public page (recommended)</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, widget_scope: 'selected' })}
                            className={`flex-1 text-left rounded-lg border p-3 transition-colors ${settings.widget_scope === 'selected' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                            data-testid="widget-scope-selected"
                          >
                            <p className="text-sm font-medium">Selected pages</p>
                            <p className="text-xs text-slate-500">Only show on pages you pick below</p>
                          </button>
                        </div>
                        {settings.widget_scope === 'selected' && (
                          <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3" data-testid="widget-pages-list">
                            {linkPages.length === 0 ? (
                              <p className="text-sm text-slate-400">No published pages found.</p>
                            ) : linkPages.map((p) => {
                              const checked = (settings.widget_paths || []).includes(p.path);
                              return (
                                <label key={p.path} className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{p.title}</p>
                                    <p className="text-xs text-slate-400 truncate">{p.path}</p>
                                  </div>
                                  <Switch
                                    checked={checked}
                                    onCheckedChange={(v) => {
                                      const cur = new Set(settings.widget_paths || []);
                                      if (v) cur.add(p.path); else cur.delete(p.path);
                                      setSettings({ ...settings, widget_paths: Array.from(cur) });
                                    }}
                                    data-testid={`widget-page-toggle-${p.path}`}
                                  />
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <Button onClick={handleSaveSettings} disabled={savingSettings} className="gap-2" data-testid="bot-settings-save">
                        {savingSettings ? 'Saving…' : 'Save settings'}
                      </Button>
                    </CardContent>
                  </Card>

                  <Card data-testid="ai-usage-card">
                    <CardHeader><CardTitle>Usage & Cost</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <Stat label="Questions answered" value={details?.usage?.total_queries ?? 0} />
                        <Stat label="Conversations" value={details?.usage?.total_sessions ?? 0} />
                        <Stat label="Active (24h)" value={details?.usage?.active_sessions_24h ?? 0} />
                        <Stat label="Cost to date" value={`$${(details?.cost?.total_cost ?? 0).toFixed(4)}`} />
                      </div>
                      <div className="mt-4 flex items-center gap-2 text-xs">
                        <span className="text-slate-500">Knowledge index:</span>
                        <span className={`px-2 py-0.5 rounded-full font-medium ${
                          details?.rag_status === 'completed' ? 'bg-emerald-100 text-emerald-700'
                          : details?.rag_status === 'indexing' ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-600'}`}>
                          {details?.rag_status || 'ready (FAQs + articles)'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* FAQ manager */}
              <Card data-testid="ai-faq-card">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle>Knowledge — FAQs</CardTitle>
                      <p className="text-sm text-slate-500">
                        Curated Q&A the assistant uses to answer. Your published articles are added automatically.
                        After changes, click <strong>Sync now</strong> above.
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsvFile} data-testid="faq-csv-input" />
                      <Button variant="outline" size="sm" className="gap-2" onClick={() => fileInputRef.current?.click()} data-testid="faq-import-btn">
                        <Upload size={14} /> Import CSV
                      </Button>
                      <button className="text-xs text-slate-400 hover:text-slate-600 underline" onClick={downloadSample} data-testid="faq-sample-btn">
                        Download template
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* CSV import confirm panel */}
                  {pendingImport && (
                    <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50 space-y-3" data-testid="faq-import-panel">
                      <p className="text-sm font-medium">Found {pendingImport.rows.length} Q&amp;A pair(s) in your CSV. How should we add them?</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => runImport('append')} data-testid="faq-import-append">Append to existing</Button>
                        <Button size="sm" variant="destructive" onClick={() => runImport('replace')} data-testid="faq-import-replace">Replace all existing</Button>
                        <Button size="sm" variant="ghost" onClick={() => setPendingImport(null)} data-testid="faq-import-cancel">Cancel</Button>
                      </div>
                    </div>
                  )}

                  {/* Add new */}
                  <div className="grid gap-3 p-4 rounded-lg border bg-slate-50">
                    <div>
                      <Label>Question</Label>
                      <Input
                        value={newFaq.question}
                        onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                        placeholder="e.g. What are your school hours?"
                        data-testid="faq-question-input"
                      />
                    </div>
                    <div>
                      <Label>Answer</Label>
                      <Textarea
                        value={newFaq.answer}
                        onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                        placeholder="e.g. Our school day runs from 8:30am to 3:15pm, Monday to Friday."
                        rows={2}
                        data-testid="faq-answer-input"
                      />
                    </div>
                    <Button onClick={handleAddFaq} className="gap-2 w-fit" data-testid="faq-add-btn">
                      <Plus size={16} /> Add FAQ
                    </Button>
                  </div>

                  {/* Existing */}
                  {faqs.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No FAQs yet. Add your first above.</p>
                  ) : (
                    <div className="space-y-2" data-testid="faq-list">
                      {faqs.map((f) => (
                        <div key={f.faq_id} className="flex items-start justify-between gap-3 p-3 rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">{f.question}</p>
                            <p className="text-sm text-slate-500">{f.answer}</p>
                          </div>
                          <Button
                            variant="ghost" size="sm" onClick={() => handleDeleteFaq(f.faq_id)}
                            data-testid={`faq-delete-${f.faq_id}`}
                          >
                            <Trash2 size={15} className="text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Documents — PDF knowledge from the Media Library */}
              <Card data-testid="ai-documents-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText size={18} /> Knowledge — Documents (PDF)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Pick PDFs from your Media Library for the assistant to read — handbooks, brochures, policies and more.
                    The assistant answers directly from their contents. Upload PDFs in the <strong>Media</strong> library first.
                  </p>
                  {!documents || documents.documents.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4" data-testid="ai-documents-empty">
                      No PDFs in your Media Library yet. Upload a PDF under Media to make it available here.
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-xs text-slate-500">
                          {documents.documents.filter((d) => d.included).length} of {documents.documents.length} document(s) included
                        </p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setAllDocuments(true)} data-testid="ai-documents-include-all-btn">Include all</Button>
                          <Button variant="outline" size="sm" onClick={() => setAllDocuments(false)} data-testid="ai-documents-exclude-all-btn">Exclude all</Button>
                        </div>
                      </div>
                      <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="ai-documents-list">
                        {documents.documents.map((d) => (
                          <div key={d.media_id} className="flex items-center justify-between gap-3 p-3 rounded-lg border">
                            <div className="min-w-0 flex items-center gap-3">
                              <FileText size={16} className="text-slate-400 shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{d.filename}</p>
                                <p className="text-xs text-slate-400 truncate">
                                  {d.size ? `${(d.size / 1024 / 1024).toFixed(1)} MB` : ''}
                                  {d.included && d.page_count ? ` · ${d.page_count} pages indexed` : ''}
                                </p>
                              </div>
                            </div>
                            <Switch
                              checked={d.included}
                              onCheckedChange={() => toggleDocument(d.media_id)}
                              data-testid={`ai-document-toggle-${d.media_id}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-start gap-2 text-xs text-slate-400">
                        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                        <span>Text-based PDFs only (max 30 MB each). Scanned/image-only PDFs can't be read.</span>
                      </div>
                      <Button onClick={handleSaveDocuments} disabled={savingDocs} className="gap-2 w-fit" data-testid="ai-documents-save-btn">
                        {savingDocs ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
                        Save documents
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Website Pages — crawl opt-out */}
              <Card data-testid="crawl-pages-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Globe size={18} /> Website Pages
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Choose which published pages the assistant is allowed to read. Turn a page off to exclude it from the assistant's knowledge.
                  </p>
                  {!crawl || crawl.pages.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4" data-testid="crawl-pages-empty">
                      No published pages or articles found yet.
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-xs text-slate-500">
                          {crawl.pages.filter((p) => p.included).length} of {crawl.pages.length} pages included
                          {crawl.last_crawled_at ? ` · last crawled ${new Date(crawl.last_crawled_at).toLocaleDateString()}` : ''}
                        </p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setAllCrawlPages(true)} data-testid="crawl-include-all-btn">Include all</Button>
                          <Button variant="outline" size="sm" onClick={() => setAllCrawlPages(false)} data-testid="crawl-exclude-all-btn">Exclude all</Button>
                        </div>
                      </div>
                      <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="crawl-pages-list">
                        {crawl.pages.map((p) => (
                          <div key={p.url} className="flex items-center justify-between gap-3 p-3 rounded-lg border">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{p.title}</p>
                              <p className="text-xs text-slate-400 truncate">{p.url}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-[10px] uppercase tracking-wide text-slate-400">{p.type}</span>
                              <Switch
                                checked={p.included}
                                onCheckedChange={() => toggleCrawlPage(p.url)}
                                data-testid={`crawl-page-toggle-${p.type}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button onClick={handleSaveCrawlPages} disabled={savingPages} className="gap-2 w-fit" data-testid="crawl-pages-save-btn">
                        {savingPages ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
                        Save & re-crawl
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="flex items-start gap-2 text-xs text-slate-400">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>Website crawling (auto-reading every page) depends on your plan. If it's not active, the assistant still answers from your FAQs and published articles.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
